import { Building2, LayoutDashboard, Trash2 } from '@tamagui/lucide-icons';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Button } from '@unistocks/components/button/button';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Input } from '@unistocks/components/input/input';
import { Column } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Paginator } from '@unistocks/components/paginator/paginator';
import { asyncStateTransition } from '@unistocks/components/query-state/async-state';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { getLocation, Table } from '@unistocks/components/table/table';
import { Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import {
  tenantGetListQueryKey,
  useTenantDeleteMutationWithParameters,
  useTenantGetListQuery
} from '@unistocks/proxy/axios-client/TenantQuery';
import { MAX_RESULT_COUNT } from '@unistocks/shared/consts';
import { usePermission } from '@unistocks/shared/hooks/use-permission';
import { t } from '@unistocks/shared/localization/index.ts';
import { UnistocksPermissions } from '@unistocks/shared/permissions';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';

export const TenantList: React.FC = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const hasEditTenant = usePermission(UnistocksPermissions.TenantManagementUpdate);
  const hasDeleteTenant = usePermission(UnistocksPermissions.TenantManagementDelete);

  const { data, isSuccess, isRefetching, refetch } = useTenantGetListQuery(
    searchText || undefined,
    undefined,
    (currentPage - 1) * MAX_RESULT_COUNT,
    MAX_RESULT_COUNT,
    { placeholderData: keepPreviousData }
  );

  const { mutate: deleteTenant, isPending: isDeleting } = useTenantDeleteMutationWithParameters({
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      queryClient.invalidateQueries({ queryKey: tenantGetListQueryKey() });
      setDeletingId(null);
    },
    onError: () => {
      toast.error(t('AbpUi::DefaultErrorMessage'));
      setDeletingId(null);
    }
  });

  const handleSearch = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const handleDelete = (tenantId: string) => {
    setDeletingId(tenantId);
    deleteTenant({ id: tenantId });
  };

  return (
    <Page size="medium">
      <Page.ScrollView
        headerTitle={t('AbpTenantManagement::Menu:TenantManagement')}
        refreshing={isRefetching}
        onRefresh={refetch}
        breadcrumbs={[
          {
            key: 'admin',
            href: '/(admin)',
            name: t('::Menu:Administration'),
            rootPathName: 'admin',
            Icon: props => <LayoutDashboard {...props} />
          }
        ]}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        <Column gap={'$3'} mt={'$3'}>
          <Input
            placeholder={t('AbpUi::PagerSearch')}
            value={searchText}
            onChangeText={handleSearch}
            $md={{
              size: '$3'
            }}
          />

          <Table
            transition="quickerLessBouncy"
            animateOnly={['opacity', 'transform']}
            enterStyle={{ o: 0, y: -20 }}
          >
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell cellLocation="first">
                  <Table.HeaderCellText>
                    {t('AbpTenantManagement::TenantName')}
                  </Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell cellLocation="middle" alignHeaderCells={{ x: 'end' }}>
                  <Table.HeaderCellText>{'ID'}</Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="last"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$2'}
                >
                  <Table.HeaderCellText>{t('AbpUi::Actions')}</Table.HeaderCellText>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>

            <QueryState
              queryKeys={[
                tenantGetListQueryKey(
                  searchText || undefined,
                  undefined,
                  (currentPage - 1) * MAX_RESULT_COUNT,
                  MAX_RESULT_COUNT
                )
              ]}
              loadingText={t('AbpUi::LoadingWithThreeDot')}
              isEmpty={isSuccess && !data.items.length}
              noDataIcon={Building2}
              noDataText={t('::NoDataAvailableInDatatable')}
            >
              <Table.Body key={'table-body'} {...asyncStateTransition}>
                {isSuccess &&
                  data.items.map((tenant, i, arr) => (
                    <Table.Row
                      key={tenant.id}
                      pressable={hasEditTenant}
                      rowLocation={getLocation(i, arr)}
                      onPress={
                        hasEditTenant
                          ? () => {
                              router.push({
                                pathname: '/(admin)/tenant-management/[id]',
                                params: { id: tenant.id }
                              });
                            }
                          : undefined
                      }
                    >
                      <Table.Cell cellLocation="first">
                        <Paragraph>{tenant.name}</Paragraph>
                      </Table.Cell>
                      <Table.Cell cellLocation="middle" alignCells={{ x: 'end' }}>
                        <Paragraph color={'$gray10'} size={'$3'}>
                          {tenant.id}
                        </Paragraph>
                      </Table.Cell>
                      <Table.ActionsCell cellWidth={'$2'}>
                        {hasDeleteTenant && (
                          <Confirm
                            title={t('::DeleteWhat', { 0: tenant.name })}
                            message={t('AbpTenantManagement::TenantDeletionConfirmationMessage', {
                              0: tenant.name
                            })}
                            onConfirm={() => handleDelete(tenant.id)}
                            isLoading={isDeleting && deletingId === tenant.id}
                          >
                            <Button
                              size={'$2'}
                              theme="red"
                              minW={'$4'}
                              Icon={Trash2}
                              loading={isDeleting && deletingId === tenant.id}
                            />
                          </Confirm>
                        )}
                      </Table.ActionsCell>
                    </Table.Row>
                  ))}
              </Table.Body>

              <Table.Foot key={'table-foot'} {...asyncStateTransition}>
                <Table.Row>
                  <Table.Cell alignCells={{ x: 'center' }}>
                    <Paginator
                      flex={1}
                      itemsPerPage={MAX_RESULT_COUNT}
                      currentPage={currentPage}
                      totalItems={data?.totalCount ?? 0}
                      onPageChange={setCurrentPage}
                    />
                  </Table.Cell>
                </Table.Row>
              </Table.Foot>
            </QueryState>
          </Table>
        </Column>
      </Page.ScrollView>
    </Page>
  );
};

TenantList.displayName = 'TenantList';
